/**
 * DashboardBanners — global announcement banners shown on the new dashboard,
 * in their own full-width row between the greeting and the widget grid.
 * Content is DB-managed (public.dashboard_banner) and date-ranged; the BE
 * returns only banners active right now.
 *
 * Up to three render side by side as equal-width blocks (one → full width,
 * two → halves, three → thirds) and stack vertically on mobile. `type` drives
 * the color. Banners with `dismissable` show a close button; dismissing one
 * stores its id in localStorage so it never shows again for this browser.
 * Non-dismissable banners have no close button and stay for their whole window.
 */

import * as React from "react";
import {
  RiInformationFill,
  RiAlertFill,
  RiErrorWarningFill,
  RiCloseLine,
} from "react-icons/ri";
import type { IconType } from "react-icons";
import { useBanners } from "./dashboard/hooks";
import type { BannerType, DashboardBanner } from "./dashboard/types";

const MAX_VISIBLE = 3;
const DISMISSED_KEY = "dashboard.dismissedBanners";

const TYPE_STYLES: Record<BannerType, string> = {
  info: "bg-blue-50 border-blue-200 text-blue-800",
  warning: "bg-amber-50 border-amber-200 text-amber-800",
  maintenance: "bg-red-50 border-red-200 text-red-800",
};

// Icon inherits the banner's text color (blue / amber / red) via currentColor.
const TYPE_ICON: Record<BannerType, IconType> = {
  info: RiInformationFill,
  warning: RiAlertFill,
  maintenance: RiErrorWarningFill,
};

function readDismissed(): number[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((x): x is number => typeof x === "number")
      : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids: number[]): void {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch {
    /* storage disabled or over quota — dismissal just won't persist */
  }
}

export default function DashboardBanners() {
  const { data } = useBanners();
  const [dismissed, setDismissed] = React.useState<number[]>(readDismissed);

  const dismiss = React.useCallback((id: number) => {
    setDismissed((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      writeDismissed(next);
      return next;
    });
  }, []);

  const banners = (data ?? [])
    .filter((b) => !(b.dismissable && dismissed.includes(b.id)))
    .slice(0, MAX_VISIBLE);

  if (banners.length === 0) return null;

  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4">
      {banners.map((b) => (
        <BannerItem key={b.id} banner={b} onDismiss={dismiss} />
      ))}
    </div>
  );
}

function BannerItem({
  banner,
  onDismiss,
}: {
  banner: DashboardBanner;
  onDismiss: (id: number) => void;
}) {
  const style = TYPE_STYLES[banner.type] ?? TYPE_STYLES.info;
  const Icon = TYPE_ICON[banner.type] ?? TYPE_ICON.info;
  return (
    <div
      // flex-1 basis-0 → every block takes an equal share of the row width.
      className={`flex-1 basis-0 min-w-0 rounded-md border px-3 py-2 text-xs leading-snug flex items-start gap-1.5 ${style}`}
    >
      <Icon className="flex-shrink-0 mt-[1px]" size={14} aria-hidden />
      <div className="min-w-0 flex-1 break-words">
        {banner.title && (
          <span className="font-semibold mr-1">{banner.title}:</span>
        )}
        <span>{banner.message}</span>
      </div>
      {banner.dismissable && (
        <button
          type="button"
          onClick={() => onDismiss(banner.id)}
          aria-label="Dismiss announcement"
          className="flex-shrink-0 -mr-1 -mt-0.5 p-0.5 rounded opacity-70 hover:opacity-100 hover:bg-black/5"
        >
          <RiCloseLine size={16} />
        </button>
      )}
    </div>
  );
}
